import { Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';

import { IColumnDefinition } from '@common/types';
import { ITaskTableEntry, ITaskTableFilters, useTaskTable } from './hooks';
import { useStableGridModels } from '@common/hooks';
import { theme } from '@store';
import { TableNoResultPlaceholder } from '@components';
import {
  TableColumnHeightMapper,
  TableHeaderHeight,
  PinnedColumnMenu,
} from 'components/TablePreferences';
import { TaskTableResetModal } from 'components/TablePreferences/TaskTableResetModal.tsx';

interface ITaskTableProps {
  rows: ITaskTableEntry[];
  columns: IColumnDefinition[];
  filters: ITaskTableFilters;
  setFilters: (filters: ITaskTableFilters) => void;
  totalCount: number;
  onSelectTask: (taskId: string) => void;
  loading: boolean;
  selectedTaskId: string;
  isCompletedTab?: boolean;
  myWorkList?: boolean;
  onResetAction?: () => void;
}

export const TaskTable = ({
  rows,
  filters,
  columns: _columns,
  setFilters,
  totalCount,
  onSelectTask,
  loading,
  myWorkList = false,
  selectedTaskId,
  isCompletedTab,
  onResetAction,
}: ITaskTableProps) => {
  const {
    apiRef,
    columns,
    preferences,
    pinnedColumns,
    visibilityModel,
    onPageModelChange,
    onDensityChange,
    handleChangeOrder,
    handlePinnedColumnsChange,
    handleUpdateVisibilityModel,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handleFullReset,
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
    initialSortFilters,
  } = useTaskTable({
    filters,
    setFilters,
    isCompletedTab,
    loading,
    onResetAction,
  });

  const { paginationModel, rowCount } = useStableGridModels({
    page: filters.page ?? 1,
    limit: filters.limit ?? 10,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    totalCount,
    loading,
  });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: rows.length === 0 ? 'calc(100vh - 450px)' : 'unset',
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          sx={{
            maxHeight: `calc(100vh - ${myWorkList ? 560 : 450}px)`,
            borderRadius: 0,
            boxShadow: 'none',
            border: theme => `1px solid ${theme.palette.grey[300]}`,
            borderBottom: 'none',
            [theme.breakpoints.down(1536)]: {
              maxHeight: 'calc(100vh - 450px)',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              display: 'flex',
            },
          }}
          slots={{
            columnMenu: props => (
              <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
            ),
            pagination: props => (
              <Box
                sx={{
                  px: 2,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  color='primary'
                  sx={{ whiteSpace: 'nowrap' }}
                  startIcon={<Refresh color='primary' />}
                  onClick={handleOpenResetViewModal}
                >
                  RESET VIEW
                </Button>
                <GridPagination {...props} />
              </Box>
            ),
            noRowsOverlay: () => (
              <Box
                sx={{
                  height: '100%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: theme => theme.palette.common.white,
                }}
              >
                <TableNoResultPlaceholder />
              </Box>
            ),
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          pagination
          paginationModel={paginationModel}
          slotProps={{
            row: {
              style: {
                cursor: 'pointer',
              },
            },
          }}
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          disableMultipleColumnsSorting
          disableRowSelectionOnClick
          paginationMode='server'
          sortingMode='server'
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          columnVisibilityModel={visibilityModel}
          columnHeaderHeight={TableHeaderHeight}
          getRowHeight={() =>
            TableColumnHeightMapper[preferences?.density ?? 'comfortable']
          }
          onColumnOrderChange={handleColumnOrderChange}
          onColumnWidthChange={handleColumnWidthChange}
          onColumnVisibilityModelChange={handleUpdateVisibilityModel}
          onDensityChange={onDensityChange}
          onPaginationModelChange={onPageModelChange}
          onSortModelChange={model => {
            if (
              model[0] &&
              (model[0].field !== filters.sort_by ||
                model[0].sort !== filters.sort_order)
            ) {
              handleChangeOrder(model[0].field, model[0].sort);
            } else {
              handleChangeOrder(
                initialSortFilters.sort_by,
                initialSortFilters.sort_order as GridSortDirection,
              );
            }
          }}
          onRowClick={params => {
            onSelectTask(params.row.id);
          }}
          getRowClassName={params => {
            const zebra =
              params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : '';
            const selected =
              params.row.id === selectedTaskId ? 'selected-task-row' : '';
            return [zebra, selected].filter(Boolean).join(' ');
          }}
        />
      </Box>
      <TaskTableResetModal
        onResetAction={handleFullReset}
        isOpen={isResetViewModalOpen}
        onClose={handleCloseResetViewModal}
      />
    </>
  );
};
